import {
  SystemBatteriesPercentageState,
  SystemEnergyState,
  SystemPowerState,
  TelemetryStatus,
} from '../models';
import { System } from '../models/firebase.model';
import { Inverter, InverterV2 } from '../telemetry';
import { Meter } from '../telemetry-graph';
import { getMeterSerial, removeMaskIdFromDeviceList, removeMaskIdMeter } from './domain.utils';
import { belowThresholdFillWithZero, minToZero } from './general.utils'
import { getFlowDistribution } from './system.utils';

export const BuildBatteriesState = (telemetries: {
  [key: string]: Partial<Inverter>;
}): SystemBatteriesPercentageState[] => Object.entries(telemetries).filter(
    ([key, v]) =>
      (v.BP !== undefined && v.BP !== null && Number(v.BP) !== 0 && !Number.isNaN(Number(v.BP))) ||
      (v.BP1 !== undefined && v.BP1 !== null && Number(v.BP1) !== 0 && !Number.isNaN(Number(v.BP1))) ||
      (v.BP2 !== undefined && v.BP2 !== null && Number(v.BP2) !== 0 && !Number.isNaN(Number(v.BP2))),
  ).map(([key, v])=> ({ deviceId: key, BP1: v.BP1, BP2: v.BP2, BP:v.BP }) );

export const BuildEnergyState= (cT: Partial<Inverter & InverterV2>): SystemEnergyState => {
  const energyDailyGenerating = Number(cT.EG1);
  const energyDailyConsuming = Number(cT.EG4);
  const energyDailyCharging = Number(cT.EG5);
  const energyDailyDischarging = Number(cT.EG6);
  const energyDailyImporting = Number(cT.EG3);
  const energyDailyExporting = Number(cT.EG2);
  const energyTotalGenerating = Number(cT.ET1);
  const energyTotalConsuming = Number(cT.ET4);
  const energyTotalCharging = Number(cT.ET5);
  const energyTotalDischarging = Number(cT.ET6);
  const energyTotalImporting = Number(cT.ET3);
  const energyTotalExporting = Number(cT.ET2);
  const energyDailyIrradiation = Number(cT.SOL_EG);
  const normalized = minToZero({
    energyDailyGenerating,
    energyDailyConsuming,
    energyDailyCharging,
    energyDailyDischarging,
    energyDailyImporting,
    energyDailyExporting,
    energyTotalGenerating,
    energyTotalConsuming,
    energyTotalCharging,
    energyTotalDischarging,
    energyTotalImporting,
    energyTotalExporting,

    EG1: Number(cT.EG1),
    EG4: Number(cT.EG4),
    EG5: Number(cT.EG5),
    EG6: Number(cT.EG6),
    EG3: Number(cT.EG3),
    EG2: Number(cT.EG2),
    ET1: Number(cT.ET1),
    ET4: Number(cT.ET4),
    ET5: Number(cT.ET5),
    ET6: Number(cT.ET6),
    ET3: Number(cT.ET3),
    ET2: Number(cT.ET2),

    SOL_EG: Number(cT.SOL_EG),
    energyDailyIrradiation,
  });
  return { ...normalized, ts: Number(cT.TS) };
};

export const BuildPowerFlow = (cT: Partial<InverterV2>): SystemPowerState => {
  /**
   * If there is only power generated by the inverter, then the power go to the home
   */
  if (!Number(cT.P_EXP) && !Number(cT.P_LOAD) && !Number(cT.P_B_AGGR)  && Number(cT.P_DC_TOT)) {
    cT.P_LOAD = cT.P_DC_TOT;
  }

  const home = Number(cT.P_LOAD);
  const solar = Number(cT.P_DC_TOT);
  const batteryPower = Math.abs(Number(cT.P_B_AGGR || 0));
  const batteryD = Number(cT.P_B_AGGR || 0) > 0 ? Number(cT.P_B_AGGR) : 0;
  const batteryS = Number(cT.P_B_AGGR || 0) < 0 ? Number(cT.P_B_AGGR) * - 1 : 0;
  const gridD = Number(cT.P_EXP || 0) > 0 ? Number(cT.P_EXP) : 0;
  const gridS = Number(cT.P_EXP || 0) < 0 ? Number(cT.P_EXP) * -1 : 0;

  const links = getFlowDistribution(home, batteryS, batteryD, solar, gridS, gridD);

  /**
   * Overwrite the powers that are below the 50W threshold with 0
   */
  const MIN_POWER_THRESHOLD_W = 50;
  const keyException = ['battery'];
  const normalized = belowThresholdFillWithZero({
    solar,
    home,
    batteryPower,
    grid:  Math.abs(Number(cT.P_EXP || 0)),
    battery: Number(cT.BP),
    fromSolarToHome: links.fromSolarToHome || 0,
    fromSolarToBattery: links.fromSolarToBattery || 0,
    fromSolarToGrid: links.fromSolarToGrid || 0,
    fromGridToHome: links.fromGridToHome || 0,
    fromBatteryToHome: links.fromBatteryToHome || 0,
    fromBatteryToGrid: links.fromBatteryToGrid || 0,
    fromGridToBattery: links.fromGridToBattery || 0,
  }, MIN_POWER_THRESHOLD_W,  keyException);
  return { ...normalized, ts: Number(cT.TS) };
};

export const getTelemetryDevices = (system: System) => {
  const { devices } = system;
  const telemetryDevices = devices
    .filter((device) => device.type === 'inverter' || device.type === 'meter' || device.type === 'enerclic')
    .map((device) => ({
      ...device,
      id: device.type === 'meter' ? getMeterSerial(device.id) : device.id,
    }),
  );

  return telemetryDevices;
};

export const processTelemetryData = async (
  data: { [key: string]: Partial<Inverter> | Partial<Inverter>[] },
): Promise<{ [key: string]: Partial<Inverter> }> => {

  const cleanData: { [key: string ]: Partial<Inverter> } = {};

  Object.keys(data).forEach((key) => {
    const deviceData = data[key];

    /**
     * 'meterEnergyQuery' and 'meterPowerQuery' are artifacts on the name of the queries, respectively for
     * retrieve energy and power data from the meter. The name of the queries does not match the identifiers of the meter as
     * the meter are identified by two numbers and a hyphen.
     *
     * @used on `BuildVirtualSystemEnergyQuery` and `BuildVirtualSystemEnergyQuery` (query-constructors.gql.ts)
     */
    if (key.match(/meterEnergyQuery/) && Array.isArray(deviceData)) {
      const meterDailyEnergies = Meter.FromTotalEnergyToDailyEnergy(removeMaskIdFromDeviceList({ [key]: deviceData }));
      const meterKey = removeMaskIdMeter(key);
      const lastMeterDailyEnergy = meterDailyEnergies && meterDailyEnergies[meterKey] && meterDailyEnergies[meterKey].slice(-1).pop() || {};
      const lastMeterTotalEnergy = deviceData.slice(-1).pop() || {};
      cleanData[meterKey] = {
        ...cleanData[meterKey] || {},
        EG2: lastMeterDailyEnergy.EG2,
        EG3: lastMeterDailyEnergy.EG3,
        ET2: lastMeterTotalEnergy.ET2,
        ET3: lastMeterTotalEnergy.ET3,
      };
    } else if(key.match(/meterPowerQuery/) && !Array.isArray(deviceData)) {
      const meterKey = removeMaskIdMeter(key);
      const lastMeterCurrentPower = deviceData;
      cleanData[meterKey] = {
        ...cleanData[meterKey] || {},
        ...lastMeterCurrentPower,
      };
    } else if (!Array.isArray(deviceData)) {
      cleanData[key] = deviceData;
    }
  });
  return cleanData;
};

export const getTelemetryStatus = (devices: string[], telemetries: { [key: string]: Partial<InverterV2> }): TelemetryStatus => {
  const UPDATED_TELEMETRY_PERIOD_THRESHOLD = 15;
  const MAX_DELAY_TELEMETRY_PERIOD_THRESHOLD = 30;

  const devicesToCheckCount = devices.length;

  const updatedThreshold = new Date();
  updatedThreshold.setMinutes(updatedThreshold.getMinutes() - UPDATED_TELEMETRY_PERIOD_THRESHOLD);
  const maxDelayAccepted = new Date();
  maxDelayAccepted.setMinutes(maxDelayAccepted.getMinutes() - MAX_DELAY_TELEMETRY_PERIOD_THRESHOLD);

  const devicesChecked = Object.values(telemetries).map((content) => {
    const TS = content?.TS;
    if (!TS) {
      return false;
    }
    const telemetryDate = new Date(TS * 1000);
    if (telemetryDate.getTime() > updatedThreshold.getTime()) {
      return true;
    }
    if (telemetryDate.getTime() > maxDelayAccepted.getTime()) {
      return null;
    }
    return false;
  });

  if (devicesChecked.length === 0) {
    return { checked: false };
  }

  if (devicesChecked.findIndex((c) => c === null) !== -1) {
    return { checked: undefined };
  }

  const devicesCheckedCount = devicesChecked.filter((checked) => checked === true).length;

  return {
    someDeviceWithoutTelemetry: Object.values(telemetries).some((ts) => ts === null),
    checked: devicesCheckedCount === devicesToCheckCount ? true : false,
  };
}

/**
 * @abstract: generate a range of 24 hours from now, util for the appsync queries
 */
export const rangeToday = (tz: string) => {
  const _from = new Date();
  return getTelemetryRange(_from.toISOString(), tz);
};

/**
 * @abstract: generate a range of 24 hours from now, util for the appsync queries for aggregate
 */
export const rangeTodayForAggregate = (tz: string) => {
  const _from = new Date();
  _from.setMinutes(0, 0, 0);
  const _to = getTelemetryRangeTo(_from.toISOString(), tz);
  return ({ from: _from.toISOString().substring(0,10), to: _to });
};

export const getTelemetryRange = (date: string, tz: string) => {
  // @note: we want _from & _to in utc (so remove the timezone)
  const _from = new Date(date);
  _from.setHours(0 - _from.getTimezoneOffset() / 60 - Number(tz) / 4);

  const _to = new Date(date);
  _to.setHours(24 - _to.getTimezoneOffset() / 60 - Number(tz) / 4);

  // Important minutes, seconds and milliseconds must be 0
  _from.setMinutes(0, 0, 0);
  _to.setMinutes(0, 0, 0);

  return ({ from: _from.toISOString(), to: _to.toISOString() });
};

export const getTelemetryRangeWithTzOnHour = (date: string, tz: string) => {
  // @note: we want _from & _to in utc (so remove the timezone)
  const _from = new Date(date);
  _from.setHours(0 - _from.getTimezoneOffset() / 60 - Number(tz));

  const _to = new Date(date);
  _to.setHours(24 - _to.getTimezoneOffset() / 60 - Number(tz));

  // Important minutes, seconds and milliseconds must be 0
  _from.setMinutes(0, 0, 0);
  _to.setMinutes(0, 0, 0);

  return ({ from: _from.toISOString(), to: _to.toISOString() });
};

/**
 * from es. "2023-05-08"
 * to es. "2023-05-08"
 * tz es. "8"
 *
 * return {from: '2023-05-07T22:00:00.000Z', to: '2023-05-08T22:00:00.000Z'}
 */
export const getTelemetryRangeFromTo = (from: string, to: string, tz: string) => {
  const _from = new Date(from);
  _from.setHours(0 - _from.getTimezoneOffset() / 60 - Number(tz) / 4);

  const _to = new Date(to);
  _to.setHours(24 - _to.getTimezoneOffset() / 60 - Number(tz) / 4);

    // Important seconds and milliseconds must be 0
  _from.setSeconds(0, 0);
  _to.setSeconds(0, 0);

  return ({ from: _from.toISOString(), to: _to.toISOString() });
};

/**
 * date es. "2023-05-08"
 * tz es. "8"
 *
 * return '2023-05-08T22:00:00.000Z'
 */
export const getTelemetryRangeTo = (date: string, tz: string) => {
  const _to = new Date(date);
  _to.setHours(24 - _to.getTimezoneOffset() / 60 - Number(tz) / 4);

  return _to.toISOString();
};
